import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { Shell } from './layout/Shell';
import { Login } from './pages/Login';
import { Today } from './pages/Today';
import { Bookings } from './pages/Bookings';
import { BookingDetail } from './pages/BookingDetail';
import { NewBooking } from './pages/NewBooking';
import { Rooms } from './pages/Rooms';
import { RoomDetail } from './pages/RoomDetail';
import { NightAudit } from './pages/NightAudit';
import { Setup } from './pages/Setup';
import { Receipt } from './pages/Receipt';
import { Accounts } from './pages/Accounts';
import { Guests } from './pages/Guests';
import { AuditLog } from './pages/AuditLog';
import { Reports } from './pages/Reports';
import { Insights } from './pages/Insights';
import { People } from './pages/People';
import { Payroll, PayrollRun } from './pages/Payroll';
import { Payslip } from './pages/Payslip';
import { MyPay } from './pages/MyPay';
import { Pool } from './pages/Pool';
import { Transport } from './pages/Transport';
import { DriverTrips } from './pages/DriverTrips';
import { Housekeeping } from './pages/Housekeeping';
import { Restaurant } from './pages/Restaurant';
import { RestaurantOrder } from './pages/RestaurantOrder';
import { BillReceipt } from './pages/BillReceipt';
import { Kitchen } from './pages/Kitchen';
import { Till } from './pages/Till';
import { MenuManager } from './pages/MenuManager';
import { Stock } from './pages/Stock';
import { Laundry } from './pages/Laundry';
import { Roster } from './pages/Roster';
import { Messages } from './pages/Messages';
import { Staff } from './pages/Staff';
import { Channels } from './pages/Channels';
import { Loading } from './ui/kit';

/** UI convenience only: the server enforces every permission regardless of what the screen shows. */
function Gate({ cap, children }: { cap: string; children: ReactElement }) {
  const { can } = useAuth();
  const allowed = cap.split('|').some((c) => can(c));
  return allowed ? children : <Navigate to="/" replace />;
}

export function App() {
  const { state } = useAuth();
  if (state.status === 'loading') {
    return (
      <div className="page">
        <Loading rows={2} />
      </div>
    );
  }
  if (state.status === 'anon') return <Login />;

  return (
    <Routes>
      <Route element={<Shell />}>
        <Route index element={<Today />} />
        <Route path="bookings" element={<Gate cap="booking.view"><Bookings /></Gate>} />
        <Route path="bookings/:id" element={<Gate cap="booking.view"><BookingDetail /></Gate>} />
        <Route path="bookings/:id/receipt" element={<Gate cap="folio.view"><Receipt /></Gate>} />
        <Route path="new" element={<Gate cap="booking.create"><NewBooking /></Gate>} />
        <Route path="restaurant" element={<Gate cap="order.create"><Restaurant /></Gate>} />
        <Route path="restaurant/orders/:id" element={<Gate cap="order.create"><RestaurantOrder /></Gate>} />
        <Route path="restaurant/bills/:id/receipt" element={<Gate cap="bill.settle"><BillReceipt /></Gate>} />
        <Route path="kitchen" element={<Gate cap="kitchen.view"><Kitchen /></Gate>} />
        <Route path="till" element={<Gate cap="shift.manage"><Till /></Gate>} />
        <Route path="menu" element={<Gate cap="menu.manage"><MenuManager /></Gate>} />
        <Route path="stock" element={<Gate cap="stock.view"><Stock /></Gate>} />
        <Route path="laundry" element={<Gate cap="laundry.view"><Laundry /></Gate>} />
        <Route path="housekeeping" element={<Gate cap="housekeeping.view"><Housekeeping /></Gate>} />
        <Route path="rooms" element={<Gate cap="inventory.view"><Rooms /></Gate>} />
        <Route path="rooms/:id" element={<Gate cap="inventory.view"><RoomDetail /></Gate>} />
        <Route path="night-audit" element={<Gate cap="ledger.view"><NightAudit /></Gate>} />
        <Route path="messages" element={<Gate cap="message.view"><Messages /></Gate>} />
        <Route path="guests" element={<Gate cap="guest.manage"><Guests /></Gate>} />
        <Route path="channels" element={<Gate cap="channel.view"><Channels /></Gate>} />
        <Route path="staff" element={<Gate cap="staff.manage"><Staff /></Gate>} />
        <Route path="audit" element={<Gate cap="audit.view"><AuditLog /></Gate>} />
        <Route path="pool" element={<Gate cap="pool.view"><Pool /></Gate>} />
        <Route path="transport" element={<Gate cap="transport.view"><Transport /></Gate>} />
        <Route path="trips" element={<Gate cap="transport.drive"><DriverTrips /></Gate>} />
        <Route path="roster" element={<Gate cap="roster.view"><Roster /></Gate>} />
        <Route path="people" element={<Gate cap="hr.view"><People /></Gate>} />
        <Route path="payroll" element={<Gate cap="payroll.view"><Payroll /></Gate>} />
        <Route path="payroll/:id" element={<Gate cap="payroll.view"><PayrollRun /></Gate>} />
        <Route path="payroll/:id/payslip/:employeeId" element={<Gate cap="payroll.view"><Payslip /></Gate>} />
        <Route path="my-pay" element={<MyPay />} />
        <Route path="my-pay/:runId" element={<Payslip />} />
        <Route path="reports" element={<Gate cap="ledger.view"><Reports /></Gate>} />
        <Route path="insights" element={<Gate cap="reports.view"><Insights /></Gate>} />
        <Route path="accounts" element={<Gate cap="ledger.view"><Accounts /></Gate>} />
        <Route path="setup" element={<Gate cap="inventory.manage|rates.manage|property.manage"><Setup /></Gate>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
